// Top-of-page banner showing active evaluation tasks.
// Drives off useJobRunnerGroup state.
//
// Per-job progress bar reads `logBytes` (polled from /api/jobs/:id) so the
// user sees real activity: bar fills as LLM streams output, and "no output
// for Xs" appears if logBytes stops growing (likely stuck request).

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, XCircle, Loader2, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Progress from '../ui/Progress';
import { apiGet } from '../../lib/api';
import { cleanLLMOutput } from '../../lib/clean-llm-output';
import type { EvalEntry, UseJobRunnerGroupResult } from '../../lib/useJobRunnerGroup';

// Authoritative queue numbers from GET /api/evaluate/queue-stats. See the
// server-side computeEvaluateQueueStats() for why the local entries list
// can't be trusted for the headline (janitor-trimmed jobs, reattach cap,
// per-session done count, 0.0/min throughput artifact).
interface QueueStats {
  pending: number;
  running: number;
  done_recent: number;
  rate_window_min: number;
  rate_per_min: number;
  eta_ms: number | null;
}

interface Props {
  group: UseJobRunnerGroupResult;
}

// Typical qwen3:32b evaluate emits ~4000–6000 chars total. Scale to 5000 so
// ~95% of runs end near 100%. Cap at 97 so the bar never says "done" until
// status actually flips — leaves 3% of visual headroom for the final flush.
const EXPECTED_BYTES = 5000;
function progressPct(e: EvalEntry): number {
  if (e.status === 'done') return 100;
  if (e.status === 'failed') return 100;
  if (e.status === 'queued') return 0;
  const bytes = e.logBytes ?? 0;
  return Math.min(97, (bytes / EXPECTED_BYTES) * 100);
}

function iconFor(status: EvalEntry['status']) {
  if (status === 'done') return <CheckCircle2 size={14} color="#2d7a4a" />;
  if (status === 'failed') return <XCircle size={14} color="#b43a3a" />;
  if (status === 'queued') return <Clock size={14} color="var(--ink3)" />;
  return <Loader2 size={14} color="var(--pri)" className="spin" />;
}

// How long without new bytes before we flag the job as possibly stuck.
const STALL_THRESHOLD_MS = 20_000;

// ETA / throughput estimate.
//
// Anchor: earliest entry.completedAt (= first observed terminal status on
// this client). This is the right reference even for a batch queued long
// before processing started — if the user queued 270 jobs 36 h ago but
// the worker only started draining them 10 min ago, throughput should
// reflect "many done in 10 min" not "62 done in 36 h ≈ 0/min".
//
// Fallback: if no entry has completedAt yet (fresh batch, nothing done),
// or all completions came in too fast to measure (<1 s window), return
// null and the headline skips the ETA suffix.
//
// We deliberately count completions WITH completedAt, not the total
// doneCount, because legacy entries done before this fix won't have the
// stamp. Throughput then reflects only what we've actually observed
// completing — slightly under-counts at first but stabilises after a
// few new completions.
function computeEta(entries: EvalEntry[], doneCount: number, failedCount: number): {
  etaMs: number | null;
  throughputPerMin: number | null;
} {
  const total = entries.length;
  if (total === 0) return { etaMs: null, throughputPerMin: null };
  // Find earliest completedAt across all entries.
  let earliestCompleted = Infinity;
  let observed = 0;
  for (const e of entries) {
    if (typeof e.completedAt === 'number') {
      observed++;
      if (e.completedAt < earliestCompleted) earliestCompleted = e.completedAt;
    }
  }
  if (observed < 2 || !Number.isFinite(earliestCompleted)) {
    return { etaMs: null, throughputPerMin: null };
  }
  const elapsedMs = Date.now() - earliestCompleted;
  if (elapsedMs <= 1000) return { etaMs: null, throughputPerMin: null };
  const remaining = total - (doneCount + failedCount);
  if (remaining <= 0) return { etaMs: null, throughputPerMin: null };
  const avgMsPerEval = elapsedMs / observed;
  return {
    etaMs: remaining * avgMsPerEval,
    throughputPerMin: (observed / elapsedMs) * 60_000,
  };
}

function fmtEta(ms: number): string {
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.round(sec / 60)} min`;
  const hr = sec / 3600;
  if (hr < 10) return `${hr.toFixed(1)} h`;
  return `${Math.round(hr)} h`;
}

// Threshold above which the banner auto-collapses to just its headline.
// Below this, the list is short enough that always-expanded is fine. At
// 6+ entries the per-entry rows start eating real estate from the page
// content (Matches, CVPicker), so we hide them by default and show a
// click-to-expand chevron — fixes the "190 evaluations covers half the
// screen" complaint.
const COLLAPSE_THRESHOLD = 5;

export default function EvaluationBanner({ group }: Props) {
  const { entries, allDone, doneCount, failedCount, cancel, cancelAll, dismiss, anyRunning } = group;

  // Server-truth queue numbers. Polled only while something is in flight.
  // The headline prefers these over the local entries-derived counts so it
  // stays honest no matter how large the queue is or how many jobs have
  // been janitor-trimmed since this tab opened.
  const statsQ = useQuery<QueueStats>({
    queryKey: ['evaluate-queue-stats'],
    queryFn: () => apiGet<QueueStats>('/api/evaluate/queue-stats'),
    enabled: anyRunning && entries.length > 0,
    refetchInterval: anyRunning ? 5000 : false,
    staleTime: 4000,
  });
  const stats = statsQ.data;

  // Server still has pending/running evaluate work? If so, never show the
  // green "Done" terminal state — even when this tab's local entries have
  // all finished (e.g. a reopened tab with a stale 200-cap reattach list
  // while 1,500 jobs are still queued server-side).
  const serverHasWork = stats ? (stats.pending + stats.running) > 0 : false;
  const showDone = allDone && !serverHasWork;

  // Banner-level expand. Auto-collapsed for big batches; user-overridable.
  // Resets on entries.length transition so a fresh batch chooses its
  // default rather than inheriting the previous batch's user choice.
  const [bannerExpanded, setBannerExpanded] = useState(entries.length <= COLLAPSE_THRESHOLD);
  useEffect(() => {
    setBannerExpanded(entries.length <= COLLAPSE_THRESHOLD);
    // Intentionally only reset on length transitions across the threshold —
    // not every length change — so adding/removing one entry mid-batch
    // doesn't toggle the banner under the user's mouse.
  }, [entries.length <= COLLAPSE_THRESHOLD]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Single-row expand: only one job's streaming markdown shows at a time so
  // we don't burn render cycles on N concurrent ReactMarkdown trees.
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const toggleExpand = (jobId: string | null) => {
    if (!jobId) return;
    setExpandedJobId((cur) => (cur === jobId ? null : jobId));
  };

  // Force a re-render every 2s so the "stalled for Xs" hint updates in real
  // time even when the polled JSON itself hasn't changed.
  const [, setClock] = useState(0);
  useEffect(() => {
    if (!anyRunning) return;
    const id = setInterval(() => setClock((n) => n + 1), 2000);
    return () => clearInterval(id);
  }, [anyRunning]);

  useEffect(() => {
    if (!showDone || entries.length === 0) return;
    const t = setTimeout(() => dismiss(), 3500);
    return () => clearTimeout(t);
  }, [showDone, entries.length, dismiss]);

  // Build the visible-entries list shown when the banner is expanded.
  //
  // Policy (2026-05-19): show ONLY entries currently consuming an LLM
  // slot (status='running'). Everything else — pending/queued (waiting
  // for a slot), done/failed (already finished) — is summarised in the
  // headline counts and intentionally not rendered.
  //
  // Why: for a 1200-job batch, the old "active + failed + a few done +
  // queued up to 30" list put 30 rows in the DOM that mostly weren't
  // doing anything. The user only acts on what's running right now
  // (cancel, expand-to-watch-LLM-stream) — the rest is just status.
  //
  // Hooks-rule note: this useMemo MUST run on every render — including
  // renders that will short-circuit to `return null` below for empty
  // entries — otherwise React throws "Rendered fewer hooks than
  // expected" and the whole subtree white-screens. The earlier version
  // had this useMemo *after* the empty-entries early return, which
  // caused exactly that crash when cancelAll() emptied the list.
  const visibleEntries = useMemo(() => {
    if (!bannerExpanded) return [];
    return entries.filter((e) => e.status === 'running');
  }, [bannerExpanded, entries]);

  if (entries.length === 0) return null;

  // Distinguish the three live states so the headline is honest:
  //   - running  = actually consuming an LLM slot RIGHT NOW (server says
  //                status='running'). Capped by MAX_CONCURRENT (typically 5).
  //   - waiting  = server-side queued (status='pending') — has a job_id,
  //                will run as soon as a worker slot frees.
  //   - queued   = local-only, no server job_id yet (bulk-POST fallback
  //                or pre-bulk dispatch loop). Rare with the current code.
  // Pre-fix: "pending" counted as "running" so a 275-URL batch said
  // "275 running" forever, hiding the fact that only ~5 actually were.
  // Local (entries-derived) counts — used for the per-row detail list and
  // as a fallback before the first server stats response lands.
  const localRunning = entries.filter((e) => e.status === 'running').length;
  const localWaiting = entries.filter((e) => e.status === 'pending').length;
  const queued = entries.filter((e) => e.status === 'queued').length;

  // Prefer SERVER truth for the headline counts. The local entries list
  // under-counts a large queue (reattach is capped at 200) and over-states
  // "done" (it only sees this tab's completions), which is what produced
  // the confusing "1580 roles · 70 done · 0.0/min · 678h" banner.
  const running = stats ? stats.running : localRunning;
  const waiting = stats ? stats.pending : localWaiting;

  // ETA / throughput. Prefer the server's report-mtime-based rate (stable,
  // not subject to client under-sampling). Fall back to the local estimate
  // only when server stats haven't arrived yet.
  let etaSuffix = '';
  if (!showDone) {
    if (stats && stats.rate_per_min > 0 && stats.eta_ms != null) {
      etaSuffix = ` · ~${fmtEta(stats.eta_ms)} left (${stats.rate_per_min.toFixed(1)}/min)`;
    } else if (stats && stats.rate_per_min === 0 && (stats.pending + stats.running) > 0) {
      // Rate genuinely measured as zero in the last window — say so plainly
      // rather than printing a misleading "0.0/min · huge h left".
      etaSuffix = ' · measuring rate…';
    } else if (!stats) {
      const { etaMs, throughputPerMin } = computeEta(entries, doneCount, failedCount);
      if (etaMs != null && throughputPerMin != null) {
        etaSuffix = ` · ~${fmtEta(etaMs)} left (${throughputPerMin.toFixed(1)}/min)`;
      }
    }
  }

  // "done" in the active headline now reflects recent server completions
  // (last N min), not this tab's session count — far less confusing than
  // "70 done" when hundreds actually finished. Total = what's left to do.
  const total = running + waiting;
  const doneRecentSuffix = (!showDone && stats && stats.done_recent > 0)
    ? ` · ${stats.done_recent} done in ${stats.rate_window_min}m`
    : '';

  const headline = showDone
    ? `Done. ${doneCount} new evaluation${doneCount === 1 ? '' : 's'} added${failedCount > 0 ? ` · ${failedCount} failed` : ''}.`
    : `Evaluating ${total} role${total === 1 ? '' : 's'}…` +
      (running > 0 ? ` ${running} running` : '') +
      (waiting > 0 ? ` · ${waiting} waiting` : '') +
      (queued > 0 ? ` · ${queued} queued` : '') +
      doneRecentSuffix +
      etaSuffix;

  // When expanded but nothing is currently running, show a one-line hint
  // instead of an empty list — explains the gap between "X waiting in
  // headline" and "no rows below". Common when all worker slots are busy
  // on long LLM calls (the running set rotates faster than the user
  // re-expands the banner) or when no slot is free yet right after the
  // bulk POST returns.
  const showIdleHint = bannerExpanded && visibleEntries.length === 0
    && (waiting > 0 || queued > 0);

  return (
    <div
      className="card"
      style={{
        marginBottom: 16,
        padding: '14px 18px',
        background: showDone ? 'rgba(45,122,74,0.04)' : 'rgba(0,0,0,0.02)',
        border: `1px solid ${showDone ? 'rgba(45,122,74,0.3)' : 'var(--line)'}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: bannerExpanded && visibleEntries.length > 0 ? 12 : 0,
        }}
      >
        {/* Clickable headline — only acts as a toggle when there are
            entries worth expanding. Tiny chevron mirrors the per-job
            ChevronUp/ChevronDown so the affordance is consistent. */}
        <button
          type="button"
          onClick={() => entries.length > 0 && setBannerExpanded((v) => !v)}
          disabled={entries.length === 0}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            cursor: entries.length > 0 ? 'pointer' : 'default',
            color: 'inherit',
            textAlign: 'left',
            flex: 1,
            minWidth: 0,
          }}
          title={
            entries.length === 0
              ? undefined
              : bannerExpanded
                ? 'Collapse details'
                : 'Show entries currently running (consuming an LLM slot)'
          }
        >
          {entries.length > 0 && (
            bannerExpanded
              ? <ChevronUp size={14} color="var(--ink3)" />
              : <ChevronDown size={14} color="var(--ink3)" />
          )}
          <span className="serif" style={{ fontSize: 14 }}>{headline}</span>
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          {anyRunning && (
            <button
              className="btn"
              style={{ padding: '4px 10px', fontSize: 11, color: '#b43a3a' }}
              onClick={cancelAll}
              title="Stop every pending/running EVALUATION. Tailor / cover letter / other LLM jobs are NOT affected — use the Debug overlay's Kill all to stop everything."
            >
              Cancel evaluations
            </button>
          )}
          <button
            className="btn"
            style={{ padding: '4px 10px', fontSize: 11 }}
            onClick={dismiss}
            title="Clear this banner and unlock affected rows. Any running evaluations stay running server-side."
          >
            {showDone ? 'Dismiss' : 'Clear'}
          </button>
        </div>
      </div>
      {bannerExpanded && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {visibleEntries.map((e) => {
          const pct = progressPct(e);
          const bytes = e.logBytes ?? 0;
          const runningish = e.status === 'running' || e.status === 'pending';
          const stalledMs = runningish && e.lastProgressAt && bytes > 0
            ? Date.now() - e.lastProgressAt
            : 0;
          const isStalled = stalledMs > STALL_THRESHOLD_MS;
          const barColor = e.status === 'failed'
            ? '#b43a3a'
            : e.status === 'done'
              ? '#2d7a4a'
              : isStalled
                ? 'var(--warn)'
                : 'var(--pri)';
          return (
            <div
              key={e.url}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                padding: '6px 10px',
                borderRadius: 6,
                background: e.status === 'failed' ? 'rgba(180,58,58,0.05)' : 'transparent',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
                {iconFor(e.status)}
                <span
                  style={{
                    flex: 1,
                    color: 'var(--ink)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={e.label}
                >
                  {e.label}
                </span>
                {runningish && bytes > 0 && (
                  <span
                    className="num"
                    style={{ fontSize: 11, color: isStalled ? 'var(--warn)' : 'var(--ink3)', minWidth: 60, textAlign: 'right' }}
                    title={`${bytes.toLocaleString()} bytes of LLM output so far`}
                  >
                    {Math.round(pct)}%
                  </span>
                )}
                {e.status === 'failed' && (
                  <span style={{ color: '#b43a3a', fontSize: 11 }}>exit {e.code ?? '?'}</span>
                )}
                {runningish && (
                  <button
                    className="btn ghost"
                    style={{ padding: '2px 8px', fontSize: 11, border: 'none', color: 'var(--ink3)' }}
                    onClick={() => cancel(e.url)}
                  >
                    cancel
                  </button>
                )}
                {/* Show output toggle — only meaningful once a jobId exists
                    (queued entries have no log yet). Single-select expand. */}
                {e.jobId && (e.status === 'running' || e.status === 'done') && (
                  <button
                    className="btn ghost"
                    style={{ padding: '2px 8px', fontSize: 11, border: 'none', color: 'var(--ink3)', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                    onClick={() => toggleExpand(e.jobId)}
                    title={expandedJobId === e.jobId ? 'Hide LLM output' : 'Show streaming LLM output (Block A-G report)'}
                  >
                    {expandedJobId === e.jobId ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                    {expandedJobId === e.jobId ? 'Hide output' : 'Show output'}
                  </button>
                )}
              </div>
              {(runningish || e.status === 'done' || e.status === 'failed') && (
                <Progress value={pct} color={barColor} style={{ height: 3 }} />
              )}
              {isStalled && (
                <div style={{ fontSize: 10.5, color: 'var(--warn)', marginLeft: 24 }}>
                  No new output for {Math.floor(stalledMs / 1000)}s — may be stuck on an Ollama queue. Click cancel if it keeps hanging.
                </div>
              )}
              {runningish && bytes === 0 && e.status === 'running' && (
                <div style={{ fontSize: 10.5, color: 'var(--ink3)', marginLeft: 24 }}>
                  Warming up — waiting on first token from Ollama…
                </div>
              )}
              {expandedJobId && expandedJobId === e.jobId && (
                <JobLogStream jobId={expandedJobId} status={e.status} />
              )}
            </div>
          );
        })}
        {showIdleHint && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink3)',
              padding: '6px 10px',
              fontStyle: 'italic',
            }}
            title="The dropdown only shows entries actively consuming a worker slot. Everything else is summarised in the headline counts."
          >
            No active workers right now — {waiting + queued} waiting for a slot.
          </div>
        )}
      </div>
      )}
    </div>
  );
}

// ── JobLogStream ─────────────────────────────────────────────────────────
// Per-job streaming markdown viewer. While the job is running we tail
// /api/jobs/:jobId/stream via SSE; when it's done we open the same endpoint
// (which replays the full log + closes immediately) to fetch the final text.
//
// Kept as a sub-component so when expandedJobId changes, React unmounts the
// previous instance, closing its EventSource cleanly. No double connections.
function JobLogStream({ jobId, status }: { jobId: string; status: EvalEntry['status'] }) {
  const [tokens, setTokens] = useState('');
  const [streamErr, setStreamErr] = useState<string | null>(null);

  useEffect(() => {
    setTokens('');
    setStreamErr(null);
    const url = `/api/jobs/${jobId}/stream`;
    const es = new EventSource(url);
    es.onmessage = (msg) => {
      try {
        const obj = JSON.parse(msg.data) as { type?: string; text?: string };
        // Only stdout/stderr text feeds the markdown — skip exit/done events.
        if ((obj.type === 'stdout' || obj.type === 'stderr') && obj.text) {
          setTokens((prev) => prev + obj.text);
        }
      } catch { /* skip malformed lines */ }
    };
    es.onerror = () => {
      // Done jobs replay then close → not a real error. Running jobs that
      // lose connection ARE errors — but EventSource auto-retries, so we
      // only flag terminal closes.
      if (es.readyState === EventSource.CLOSED) {
        // Silent close on terminal job is normal. Surface only if running.
        if (status === 'running' || status === 'pending') {
          setStreamErr('connection lost — output may be incomplete');
        }
      }
    };
    return () => { es.close(); };
  }, [jobId, status]);

  const cleanLog = useMemo(() => cleanLLMOutput(tokens), [tokens]);

  return (
    <div
      className="report-md"
      style={{
        marginTop: 8,
        marginLeft: 24,
        padding: '12px 14px',
        background: 'rgba(0,0,0,0.025)',
        border: '1px solid var(--line2)',
        borderRadius: 6,
        fontSize: 12.5,
        lineHeight: 1.65,
        color: 'var(--ink2)',
        maxHeight: 400,
        overflowY: 'auto',
      }}
    >
      {cleanLog ? (
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleanLog}</ReactMarkdown>
      ) : (
        <span style={{ color: 'var(--ink3)', fontStyle: 'italic' }}>
          {status === 'running' ? 'Streaming…' : 'No output captured.'}
        </span>
      )}
      {streamErr && (
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--warn)' }}>{streamErr}</div>
      )}
    </div>
  );
}
